import AuthenticatedSessionController from '@/actions/App/Http/Controllers/Admin/Auth/AuthenticatedSessionController';
import PasswordInput from '@/components/forms/password-input';
import InputError from '@/components/shared/input-error';
import TextLink from '@/components/shared/text-link';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import AuthSplitLayout from '@/layouts/auth/auth-split-layout';
import React from 'react';
// import { register } from '@/routes';
import { request } from '@/routes/admin/password';
import { Form, Head } from '@inertiajs/react';
import { ArrowRight } from 'lucide-react';

interface LoginProps {
    status?: string;
    canResetPassword: boolean;
}

export default function Login({ status, canResetPassword }: LoginProps) {
    const [remember, setRemember] = React.useState(false);

    return (
        <AuthSplitLayout
            title="Welcome back"
            description="Sign in to manage bookings, rooms and guests."
            image="/images/admin-login-bg.webp"
        >
            <Head title="Sign in" />

            {status && (
                <div className="rounded-lg border border-green-300/40 bg-green-400/15 px-4 py-3 text-sm font-medium text-green-100 dark:border-green-900/40">
                    {status}
                </div>
            )}

            <Form
                action={AuthenticatedSessionController.store.url()}
                method="post"
                resetOnSuccess={['password']}
                className="flex flex-col gap-6"
            >
                {({ processing, errors }) => (
                    <>
                        <div className="grid gap-2">
                            <Label htmlFor="email" className="text-white">
                                Email address
                            </Label>
                            <Input
                                id="email"
                                type="email"
                                name="email"
                                required
                                autoFocus
                                tabIndex={1}
                                autoComplete="email"
                                placeholder="email@example.com"
                                className="h-11 border-white/25 bg-white/10 text-white placeholder:text-white/50 focus-visible:border-white/60 focus-visible:ring-white/20"
                            />
                            <InputError
                                message={errors.email}
                                className="text-red-300"
                            />
                        </div>

                        <div className="grid gap-2">
                            <div className="flex items-center justify-between">
                                <Label
                                    htmlFor="password"
                                    className="text-white"
                                >
                                    Password
                                </Label>
                                {canResetPassword && (
                                    <TextLink
                                        href={request()}
                                        className="text-sm text-white/80 decoration-white/30 hover:text-white"
                                        tabIndex={5}
                                    >
                                        Forgot password?
                                    </TextLink>
                                )}
                            </div>
                            <PasswordInput
                                id="password"
                                name="password"
                                required
                                tabIndex={2}
                                autoComplete="current-password"
                                placeholder="Password"
                                className="h-11 border-white/25 bg-white/10 pr-11 text-white placeholder:text-white/50 focus-visible:border-white/60 focus-visible:ring-white/20"
                                iconClassName="fill-current"
                                toggleClassName="text-white/70 focus:outline-none"
                                skipToggleTab
                            />
                            <InputError
                                message={errors.password}
                                className="text-red-300"
                            />
                        </div>

                        <div className="flex items-center gap-3">
                            <input
                                type="hidden"
                                name="remember"
                                value={remember ? '1' : '0'}
                            />
                            <Checkbox
                                id="remember"
                                className="border-white/40"
                                checked={remember}
                                onCheckedChange={(checked) =>
                                    setRemember(checked === true)
                                }
                                tabIndex={3}
                            />
                            <Label
                                htmlFor="remember"
                                className="font-normal text-white/80"
                            >
                                Keep me signed in
                            </Label>
                        </div>

                        <Button
                            type="submit"
                            className="h-11 w-full cursor-pointer text-base"
                            tabIndex={4}
                            disabled={processing}
                            data-test="login-button"
                        >
                            {processing ? <Spinner /> : null}
                            Sign in
                            {!processing && <ArrowRight className="size-4" />}
                        </Button>

                        {/* <div className="text-center text-sm text-muted-foreground">
                            Don't have an account?{' '}
                            <TextLink href={register()} tabIndex={5}>
                                Create account
                            </TextLink>
                        </div> */}
                    </>
                )}
            </Form>
        </AuthSplitLayout>
    );
}
